import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title="Privacy Policy | Frayze Technologies"
        description="Frayze respects your privacy. Learn how we collect, use, and protect your personal information in connection with our services."
        keywords="privacy policy, data protection, frayze privacy, gdpr, pipeda"
        canonical="https://frayze.ca/privacy-policy"
        robots="noindex, follow"
      />
      <Header />

      <main className="pt-40 pb-20 px-6">
        <div className="max-w-4xl mx-auto prose prose-slate dark:prose-invert">
          <h1>Privacy Policy (Updated)</h1>
          <p className="text-muted-foreground">Effective Date: {currentDate}</p>

          <p>
            Frayze Technologies Inc. (“Frayze,” “we,” “us,” or “our”) respects your privacy and is committed to protecting it. This Privacy Policy describes how we collect, use, share and safeguard personal information in connection with our websites, platform, and services.
          </p>

          <h2>1. Information We Collect</h2>
          <p>
            We collect personal information you provide directly (e.g., name, email, phone, business details), payment details to process subscriptions and usage charges, and any content you share during onboarding or support. Through your use of our CRM and automation platform we may also collect customer names, contact details, lead source data, form submissions, messaging history (SMS/email/chat), AI scoring metrics and internal communications.
          </p>
          <p>
            Automatically collected data includes IP address, browser type, device ID, referral URLs, geolocation and usage patterns via cookies and analytics tools.
          </p>

          <h2>2. How We Use Information</h2>
          <p>
            We use personal information to provide and improve our CRM, marketing automation and AI services; personalize user experiences; conduct marketing campaigns; analyze performance metrics; detect fraud; and meet legal obligations. AI algorithms may score leads, predict engagement and auto‑generate messages; results assist human judgment, and clients can request human review.
          </p>

          <h2>3. Data Sharing</h2>
          <p>
            We do not sell your data. We share it with trusted service providers for hosting, analytics, email/SMS delivery, payment processing, customer support, marketing and advertising (e.g., Meta, Google Ads) and with third‑party integrations necessary to deliver our services (e.g., OpenAI, Make, n8n). Data may be transferred to cloud and infrastructure providers (e.g., AWS). We may disclose information to comply with legal obligations or to protect rights, property or safety.
          </p>

          <h2>4. Cookies and Tracking</h2>
          <p>
            We use cookies, pixels and web beacons to understand site usage, deliver advertising and measure campaign performance. You may opt out via browser settings or the Google Analytics Opt‑Out browser add‑on.
          </p>

          <h2>5. Usage & Billing Data</h2>
          <p>
            Our platform operates on a hybrid pricing model with subscription fees, included usage credits and usage‑based billing for communications (e.g., SMS, calls, emails) and AI interactions. Usage is monitored to calculate charges and prevent abuse. See our Usage & Billing Policy for full details.
          </p>

          <h2>6. Data Retention</h2>
          <p>
            We retain personal data as long as necessary to fulfill the purposes outlined herein, comply with legal obligations or enable future reactivation/marketing; CRM data may be stored for up to 36 months unless deletion is requested. Creative Work Product (websites, designs, code, copy and creative assets) produced by Frayze is retained indefinitely as the exclusive property of Frayze Technologies Inc., as outlined in our Terms of Service.
          </p>

          <h2>7. Your Rights</h2>
          <p>
            You may request access, correction, deletion or export of your personal and business data (e.g., contacts, form submissions, analytics); withdraw consent; or object to processing for marketing purposes. These rights apply to your personal and business data only and do not extend to Creative Work Product (websites, designs, code, copy, branding and creative assets), which is the exclusive intellectual property of Frayze Technologies Inc. as described in our Terms of Service. To exercise your data rights, contact <a href="mailto:privacy@frayze.ca" className="text-primary hover:underline">privacy@frayze.ca</a>.
          </p>

          <h2>8. Data Security</h2>
          <p>
            Frayze implements encryption, secure hosting, firewalls, role‑based access and regular audits. However, no method of transmission is 100% secure.
          </p>

          <h2>9. International Transfers</h2>
          <p>
            Where data is transferred across borders, we comply with data protection laws (e.g., Standard Contractual Clauses).
          </p>

          <h2>10. Children’s Privacy</h2>
          <p>
            Our services are not intended for children under 16.
          </p>

          <h2>11. Business Transfers</h2>
          <p>
            If we undergo a merger, acquisition or sale, personal data may be transferred to the new entity; we will notify you of changes.
          </p>

          <h2>12. User‑Generated Content</h2>
          <p>
            We may display client testimonials or user‑generated content publicly with names; you may request removal or anonymization.
          </p>

          <h2>12a. Creative Work Product &amp; Intellectual Property</h2>
          <p>
            All websites, web designs, graphic designs, custom code, copywriting, branding materials, creative assets, marketing content and any other deliverables produced by Frayze Technologies Inc. on your behalf ("Creative Work Product") are and shall remain the exclusive property of Frayze Technologies Inc. Clients receive a limited, non‑exclusive, revocable license to use Creative Work Product while their account is active and payments are current, as described in our Terms of Service. Frayze may collect, store and process data related to Creative Work Product for the purposes of service delivery, portfolio use, quality assurance and business operations. No Creative Work Product is sold, assigned or transferred to any client at any point.
          </p>

          <h2>13. Data Breach Notification</h2>
          <p>
            We will notify affected users and authorities of any data breach as required by law.
          </p>

          <h2>14. Changes & Contact</h2>
          <p>
            We may update this policy; material changes will be posted on our website. Questions? Contact <a href="mailto:privacy@frayze.ca" className="text-primary hover:underline">privacy@frayze.ca</a> or write to Frayze Technologies Inc., [address].
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;